// @ts-check
// =============================================================================
// js/ui/profile.js — the player profile popup.
//
// WHAT THIS IS
//   Opened from the nav shell's welcome chip (👤). Today it owns the player's
//   public identity: avatar (interim: your selected deck's leader face),
//   nickname (rename lives HERE now), tier, and the basic score/level stats.
//   PFP picking, friends, and a bio are planned — they get labeled
//   placeholder rows so the popup's shape is already right.
//
// RENAME (mirrors the old settings flow)
//   Every /request carries `nickname`; the server persists a changed one for
//   members (request-router "Honor a nickname change"). So renaming is:
//   mutate store.userData.nickname, notify, fire any cheap request ('Lobby').
//   Guests: their account IS the request body, so a rename works for the
//   session but resets on their next visit — the popup says so.
// =============================================================================

import { store, notify, setSlice } from '../core/store.js';
import { request } from '../net/http.js';
import { openModal, escapeHtml } from './modal.js';
import { toast } from './toast.js';
import { HERO_ASSET_NAMES } from './deck-list.js';

/** Public tier labels (session.tier is guest/cheap/paid). */
const TIER_LABELS = { guest: 'Guest', cheap: 'Member', paid: 'Subscriber' };

/**
 * Open the profile popup for the logged-in player.
 * @returns {void}
 */
export function openProfile() {
	const userData = store.userData;
	if (!userData) return;

	const body = document.createElement('div');
	body.className = 'profile-box';
	body.append(buildHead(userData), buildRenameRow(userData), buildStats(userData), buildComingSoon());

	openModal({ body, buttons: [{ label: 'Close' }] });
}

/**
 * Avatar + name + tier. The avatar is the selected deck's leader face until
 * real profile pictures exist.
 * @param {any} userData
 * @returns {HTMLElement}
 */
function buildHead(userData) {
	const head = document.createElement('div');
	head.className = 'profile-head';
	const face = HERO_ASSET_NAMES[store.loadedDeck?.hero] || 'john';
	head.innerHTML = `
		<img class="profile-avatar" src="/leaders/faces/${face}.png" alt="Profile picture">
		<div class="profile-id">
			<div class="profile-nickname">${escapeHtml(userData.nickname || 'Biter')}</div>
			<div class="profile-tier">${TIER_LABELS[store.session.tier] || 'Player'}</div>
		</div>`;
	return head;
}

/**
 * The rename row: input + Save.
 * @param {any} userData
 * @returns {HTMLElement}
 */
function buildRenameRow(userData) {
	const row = document.createElement('div');
	row.className = 'profile-rename';

	// Guests are shown as GUEST#### and cannot rename — a clear "you're a guest"
	// nudge to sign up instead of an editable field.
	if (store.session.isGuest) {
		row.innerHTML = `<div class="profile-note">You're playing as a guest. Log in to choose your own name and keep your progress!</div>`;
		return row;
	}

	row.innerHTML = `
		<label class="profile-label" for="profile-nickname-input">Your Name</label>
		<div class="profile-rename-row">
			<input id="profile-nickname-input" class="textInput" maxlength="24"
				value="${escapeHtml(userData.nickname || '')}">
			<button class="defaultButton" id="profile-rename-btn">Save</button>
		</div>`;

	row.querySelector('#profile-rename-btn')?.addEventListener('click', () => {
		const input = /** @type {HTMLInputElement|null} */ (row.querySelector('#profile-nickname-input'));
		const name = input?.value.trim();
		if (!name || name === store.userData.nickname) return;
		store.userData.nickname = name;
		notify('userData'); // nav chip + this popup's header
		const shownName = row.closest('.profile-box')?.querySelector('.profile-nickname');
		if (shownName) shownName.textContent = name;
		// Any request persists the new nickname server-side (see header note);
		// 'Lobby' is the cheapest and its response refreshes the duel list too.
		void request('Lobby').then((returned) => {
			if (returned?.duels) setSlice('lobby', { ...store.lobby, duels: returned.duels });
		});
		toast('Name updated!');
	});
	return row;
}

/**
 * The score/level stats block (userData.stats.casual / .competitive).
 * @param {any} userData
 * @returns {HTMLElement}
 */
function buildStats(userData) {
	const stats = userData.stats || {};
	const games = (mode) => (Array.isArray(mode?.games) ? mode.games.reduce((a, b) => a + (Number(b) || 0), 0) : 0);
	const rows = [
		{ label: 'Casual', level: stats.casual?.level || 0, score: stats.casual?.score || 0, games: games(stats.casual) },
		{ label: 'Competitive', level: stats.competitive?.level || 0, score: stats.competitive?.score || 0, games: games(stats.competitive) },
	];

	const wrap = document.createElement('div');
	wrap.className = 'profile-stats';
	wrap.innerHTML = `
		<div class="profile-label">Stats</div>
		${rows.map((row) => `
			<div class="profile-stat-row">
				<span class="profile-stat-mode">${row.label}</span>
				<span>Level ${row.level}</span>
				<span>Score ${row.score}</span>
				<span>${row.games} game${row.games === 1 ? '' : 's'}</span>
			</div>`).join('')}`;
	return wrap;
}

/**
 * Placeholders for the planned profile features.
 * @returns {HTMLElement}
 */
function buildComingSoon() {
	const wrap = document.createElement('div');
	wrap.className = 'profile-soon';
	wrap.innerHTML = `
		<div class="profile-label">Coming Soon</div>
		<div class="profile-soon-chips">
			<span class="profile-soon-chip">🖼️ Profile Pic</span>
			<span class="profile-soon-chip">🤝 Friends</span>
			<span class="profile-soon-chip">📝 Bio</span>
		</div>`;
	return wrap;
}
